import { Eye, Lock, LockOpen, RefreshCw, ShieldAlert } from "lucide-react";

const STATUS_META = {
  open: {
    label: "Open for editing",
    badge: "bg-emerald-100 text-emerald-900 border-emerald-200",
    icon: LockOpen,
  },
  no_pay_period: {
    label: "Open for editing",
    badge: "bg-slate-100 text-slate-800 border-slate-200",
    icon: LockOpen,
  },
  locked: {
    label: "Locked — payroll finalized",
    badge: "bg-red-100 text-red-900 border-red-200",
    icon: Lock,
  },
  correction_open: {
    label: "Correction window open",
    badge: "bg-amber-100 text-amber-950 border-amber-300",
    icon: LockOpen,
  },
};

export default function AttendanceLockPanel({
  lockStatus,
  loading,
  busy,
  isAdmin,
  lockError,
  onOpenCorrection,
  onCloseCorrection,
}) {
  if (!lockStatus && !loading) return null;

  const status = lockStatus?.status || "open";
  const meta = STATUS_META[status] || STATUS_META.open;
  const Icon = meta.icon;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 md:p-5 mb-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`p-2.5 rounded-xl border shadow-sm ${
            status === "locked"
              ? "bg-red-50 border-red-200"
              : status === "correction_open"
                ? "bg-amber-50 border-amber-200"
                : "bg-emerald-50 border-emerald-200"
          }`}>
            <Icon className={`w-5 h-5 ${
              status === "locked"
                ? "text-red-700"
                : status === "correction_open"
                  ? "text-amber-700"
                  : "text-emerald-700"
            }`} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Month locking status
            </p>
            <h2 className="text-base md:text-lg font-bold text-slate-900 mt-0.5">
              {loading ? "Checking lock status…" : meta.label}
            </h2>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              {loading ? "Loading payroll lock details for this month." : lockStatus?.message}
            </p>
          </div>
        </div>
      </div>

      {!loading && lockStatus ? (
        <dl className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2.5 mt-4 text-sm border-t border-slate-100 pt-4">
          <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
            <dt className="text-slate-500 text-xs">Pay period</dt>
            <dd className="font-medium text-slate-900 mt-0.5">
              {lockStatus.period_start && lockStatus.period_end
                ? `${lockStatus.period_start} → ${lockStatus.period_end}`
                : "—"}
            </dd>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
            <dt className="text-slate-500 text-xs">Attendance editing</dt>
            <dd className="font-medium text-slate-900 mt-0.5">
              {lockStatus.can_edit_attendance ? "Allowed" : "Blocked"}
            </dd>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
            <dt className="text-slate-500 text-xs">Calendar access</dt>
            <dd className="font-medium text-slate-900 mt-0.5">
              {lockStatus.can_view_attendance !== false ? (
                <span className="inline-flex items-center gap-1.5 text-teal-800">
                  <Eye className="w-3.5 h-3.5" />
                  View only
                </span>
              ) : (
                "—"
              )}
            </dd>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
            <dt className="text-slate-500 text-xs">Payroll run</dt>
            <dd className="font-medium text-slate-900 mt-0.5">
              {lockStatus.payroll_run_status || "—"}
              {lockStatus.payroll_lifecycle_status
                ? ` · ${lockStatus.payroll_lifecycle_status}`
                : ""}
            </dd>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
            <dt className="text-slate-500 text-xs">Locked at</dt>
            <dd className="font-medium text-slate-900 mt-0.5">
              {lockStatus.locked_at ? new Date(lockStatus.locked_at).toLocaleString() : "—"}
            </dd>
          </div>
        </dl>
      ) : null}

      {status === "locked" ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex gap-2">
          <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Attendance is locked for this month</p>
            <p className="mt-1 text-amber-800">
              Day changes are blocked because payroll was processed. You can still view the
              employee calendar below in read-only mode. An admin can open a correction window
              to edit Present / Absent / Leave, then lock again and refresh payroll totals.
            </p>
          </div>
        </div>
      ) : null}

      {lockError ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {lockError}
        </div>
      ) : null}

      {status === "correction_open" ? (
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="font-semibold">Correction window is active</p>
          <ol className="mt-2 list-decimal list-inside space-y-1 text-amber-900">
            <li>Tap calendar days to change Present, Absent, Leave, etc.</li>
            <li>When finished, click <b>Lock &amp; refresh payroll</b> to recalculate from edited days.</li>
            <li>Attendance will lock again automatically after payroll refresh.</li>
          </ol>
        </div>
      ) : null}

      {isAdmin && !loading && lockStatus?.can_open_correction ? (
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={onOpenCorrection}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-60"
          >
            <LockOpen className="w-4 h-4" />
            Open correction window
          </button>
        </div>
      ) : null}

      {isAdmin && !loading && lockStatus?.can_close_correction ? (
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={onCloseCorrection}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-teal-700 hover:bg-teal-800 disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
            Lock &amp; refresh payroll
          </button>
        </div>
      ) : null}
    </div>
  );
}
